// PRD 06 §2.1 / §3.3 — 주차별 캘린더 tab body. Read-only (ADM.009 — no week
// click / popup). Renders a `monitoringWeeks`-row table with 7 weekday columns
// + a total column. Monthly weeks (1, 5, 9, 13, 17, 21, 25) get a row tint +
// 매월 label so the admin can see when monthly modules open.
//
// The weekly workload threshold/warning colouring was removed per ADM.008
// (feature dropped from scope).

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { WEEKDAY_ORDER, computeCalendar } from '../utils/calendar';
import type { Module, ProItemRef, Weekday } from '../types/pro-deployment.types';

interface WeekCalendarTableProps {
  modules: Module[];
  items: ProItemRef[];
  monitoringWeeks: number;
}

export function WeekCalendarTable({
  modules,
  items,
  monitoringWeeks,
}: WeekCalendarTableProps) {
  const { t } = useTranslation('pro-deployment');
  const rows = useMemo(
    () => computeCalendar(modules, items, monitoringWeeks),
    [modules, items, monitoringWeeks],
  );

  return (
    <>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table
            className="table"
            style={{ margin: 0, fontSize: 'var(--font-size-xs)' }}
          >
            <thead>
              <tr>
                <th style={{ minWidth: 56, textAlign: 'center' }}>
                  {t('calendar.weekColumn')}
                </th>
                {WEEKDAY_ORDER.map((day) => (
                  <th key={day} style={{ minWidth: 48, textAlign: 'center' }}>
                    {t(`calendar.weekdays.${day}`)}
                  </th>
                ))}
                <th style={{ minWidth: 56, textAlign: 'center', fontWeight: 700 }}>
                  {t('calendar.totalColumn')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.week}
                  style={row.isMonthly ? { background: '#fefce8' } : undefined}
                >
                  <td
                    style={{
                      textAlign: 'center',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-xs)',
                    }}
                  >
                    {t('calendar.weekFormat', { n: row.week })}
                    {row.isMonthly ? (
                      <>
                        <br />
                        <span
                          style={{
                            fontSize: 10,
                            color: 'var(--badge-warning-text)',
                          }}
                        >
                          {t('calendar.monthlyTag')}
                        </span>
                      </>
                    ) : null}
                  </td>
                  {WEEKDAY_ORDER.map((day) => (
                    <DayCell key={day} value={row.days[day]} weekday={day} />
                  ))}
                  <TotalCell value={row.total} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div
        style={{
          marginTop: 'var(--space-4)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--muted-foreground)',
          lineHeight: 1.8,
        }}
      >
        <p style={{ margin: 0 }}>{t('calendar.footerExcludeDaily')}</p>
        <p style={{ margin: 0 }}>{t('calendar.footerFreeResponse')}</p>
      </div>
    </>
  );
}

interface DayCellProps {
  value: number;
  weekday: Weekday;
}

function DayCell({ value, weekday }: DayCellProps) {
  const style: React.CSSProperties = {
    textAlign: 'center',
    fontSize: 'var(--font-size-xs)',
  };
  return (
    <td style={style} aria-label={`${weekday} ${value}`}>
      {value > 0 ? value : <span style={{ color: 'var(--border)' }}>-</span>}
    </td>
  );
}

function TotalCell({ value }: { value: number }) {
  const style: React.CSSProperties = {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: 'var(--font-size-xs)',
  };
  return <td style={style}>{value}</td>;
}
